import { AppBar, Button, ButtonBase, IconButton } from '@mui/material';
import { Box, styled } from '@mui/system';

import {
  BORDER, CONTROL_TEXT, FOCUS_RING, HEADER_CONTENT_HEIGHT, HOVER_BG, MUTED,
  SANS_STACK, SUCCESS, TEXT_SECONDARY,
} from '../common/tokens';

export const StyledAppBar = styled(AppBar)({
  backgroundColor: 'white',
  height: HEADER_CONTENT_HEIGHT,
  justifyContent: 'center',
});

// Dropped in zen mode.
export const HeaderSpacerBox = styled(Box)({
  height: HEADER_CONTENT_HEIGHT,
  flexShrink: 0,
});

// The header's general-purpose button: add, search, and the two zoom controls.
export const AddChip = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  height: '28px',
  padding: '0 8px',
  borderRadius: '6px',
  fontFamily: SANS_STACK,
  fontSize: '.85rem',
  fontWeight: 700,
  letterSpacing: 'normal',
  color: TEXT_SECONDARY,
  backgroundColor: HOVER_BG,
  '& .MuiSvgIcon-root': { fontSize: 16 },
  '&:hover': {
    backgroundColor: theme.palette.primary.tint,
    color: CONTROL_TEXT,
  },
  '&.Mui-disabled': {
    opacity: 0.3,
    pointerEvents: 'none',
  },
}));

export const ModerationChip = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '6px 9px',
  marginRight: '6px',
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '6px',
  background: active ? theme.palette.primary.main : '#fff',
  color: active ? '#fff' : theme.palette.primary.main,
  fontFamily: SANS_STACK,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': {
    background: active ? theme.palette.primary.dark : theme.palette.primary.tint,
  },
}));

export const LoginButton = styled(Button)(({ theme }) => ({
  margin: '0 8px',
  padding: '7px 14px',
  lineHeight: 1.15,
  border: `1.5px solid ${theme.palette.primary.main}`,
  borderRadius: '6px',
  color: theme.palette.primary.main,
  fontSize: '0.85rem',
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    border: `1.5px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.tint,
  },
}));

export const AvatarButton = styled(IconButton)({
  padding: 0,
  marginLeft: '8px',
});

// The rounded field the search and add-URL boxes are built in.
export const ExpandingBoxShell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  overflow: 'hidden',
  boxSizing: 'border-box',
  transition: 'width 0.3s ease-in-out',
  width: '700px',
  borderRadius: '8px',
  border: `1.5px solid ${BORDER}`,
  height: '38px',
  backgroundColor: 'white',
  marginLeft: '8px',
  '&:focus-within': {
    borderColor: SUCCESS,
    boxShadow: FOCUS_RING,
  },
});

export const ExpandingBoxInput = styled('input')(({ theme }) => ({
  flex: 1,
  border: 'none',
  outline: 'none',
  padding: '6px 14px',
  fontSize: '1rem',
  fontFamily: 'inherit',
  backgroundColor: 'transparent',
  '&::placeholder': {
    color: theme.palette.grey[500],
  },
}));

// Geometry shared by the two boxes' trailing action buttons. They diverge on
// their content: the search box submits with an icon, the add box with a "+"
// glyph that needs type styling of its own.
const boxActionButton = (theme) => ({
  color: theme.palette.primary.main,
  padding: '3px',
  flexShrink: 0,
  minWidth: '32px',
  minHeight: '32px',
  borderRadius: '6px',
  '&:hover': { backgroundColor: theme.palette.primary.tint },
});

export const SearchIconButton = styled(IconButton)(({ theme }) => ({
  ...boxActionButton(theme),
  '& .MuiSvgIcon-root': {
    strokeWidth: 1,
    stroke: theme.palette.primary.main,
  },
}));

export const AddIconButton = styled(IconButton)(({ theme }) => ({
  ...boxActionButton(theme),
  fontWeight: 'bold',
  fontSize: '1.2rem',
}));

export const CLEAR_BUTTON_SX = {
  color: 'grey.400',
  borderRadius: '6px',
  '&:hover': { backgroundColor: HOVER_BG },
};

export const SORT_SELECT_SX = {
  borderRadius: '6px',
  fontFamily: SANS_STACK,
  fontSize: '.85rem',
  fontWeight: 700,
  letterSpacing: 'normal',
  height: '28px',
  color: CONTROL_TEXT,
  backgroundColor: HOVER_BG,
  '&.MuiInputBase-root': { paddingLeft: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-icon': { color: MUTED, fontSize: 16, right: '4px' },
  '& .MuiSelect-select': { paddingRight: '24px !important' },
  '&:hover': { backgroundColor: 'primary.tint' },
};

export const SORT_PREFIX_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: MUTED,
  marginRight: '7px',
  whiteSpace: 'nowrap',
};
